import { Region } from '@bringg/frontend-utils/dist/common/types';
import {
	ScheduleUpdateParams,
	RateParams,
	SendNoteParams,
	ForwardSharedLocationParams,
	UpdateWayPointLocation,
	CarArrivedData,
	DriverActivity,
	MaskedPhoneResponse,
	ContactNotificationsOptions,
	OldSharedLocationConfig,
	CustomerFlowType,
	RescheduleOption,
	RescheduleOptionsResponse
} from '../types/shared-location.types';
import { getEnv } from 'mobx-easy';
import { RootEnv } from '../helpers/create-store';
import _isEmpty from 'lodash/isEmpty';
import * as dayjs from 'dayjs';
import { Vehicle } from '@bringg/types';

export class SharedLocationService {
	async getSharedLocation(sharedLocationUuid: string): Promise<OldSharedLocationConfig> {
		const url = this.getSharedLocationUrl(sharedLocationUuid);
		const response = await fetch(url, { headers: this.headers });

		if (!response.ok) {
			throw new Error(`Failed to get shared location ${sharedLocationUuid}, status: ${response.status}`);
		}

		return response.json();
	}

	async getMaskedPhone(sharedLocationUuid: string): Promise<string> {
		const url = this.getMaskedPhoneUrl(sharedLocationUuid);
		const response = await fetch(url, { headers: this.headers });

		if (!response.ok) {
			throw new Error(`Failed to get masked phone ${sharedLocationUuid}, status: ${response.status}`);
		}

		const result: MaskedPhoneResponse = await response.json();

		if (result.status !== 'ok') {
			throw new Error(`Failed to get masked phone ${sharedLocationUuid}, status: ${result.status}`);
		}

		return result.phone_number;
	}

	async getRescheduleOptions(rescheduleOptionsUrl: string): Promise<RescheduleOption[]> {
		const response = await fetch(rescheduleOptionsUrl, { headers: this.headers });

		if (!response.ok) {
			throw new Error(`Failed to get reschedule options, status: ${response.status}`);
		}

		const result: RescheduleOptionsResponse = await response.json();

		if (result.success !== true) {
			throw new Error('Failed to get reschedule options');
		}

		return result.reschedule_options ?? [];
	}

	async updateReschedule(scheduleUpdateParams: ScheduleUpdateParams, updateRescheduleUrl: string) {
		const response = await this.post(updateRescheduleUrl, scheduleUpdateParams);

		if (!response.ok) {
			throw new Error(`Failed to update reschedule, status: ${response.status}`);
		}

		const result = await response.json();

		if (result.success !== true) {
			throw new Error('Failed to update reschedule');
		}

		return result;
	}

	async getVehicles(sharedLocationUuid: string): Promise<Vehicle[]> {
		const url = this.getVehiclesUrl(sharedLocationUuid);
		const response = await fetch(url, { headers: this.headers });

		if (!response.ok) {
			throw new Error(`Failed to get vehicles ${sharedLocationUuid}, status: ${response.status}`);
		}

		const result = await response.json();

		if (result.success !== true) {
			throw new Error(`Failed to get vehicles ${sharedLocationUuid}, status: ${response.status}`);
		}

		return result.vehicles;
	}

	async schedule(scheduleUpdateParams: ScheduleUpdateParams, sharedLocationUuid: string) {
		const url = this.getScheduleUrl(sharedLocationUuid);
		const response = await fetch(url, {
			method: 'POST',
			body: JSON.stringify(scheduleUpdateParams),
			headers: this.headers
		});

		if (!response.ok) {
			throw new Error(`Failed to schedule ${sharedLocationUuid}, status: ${response.status}`);
		}

		const result = await response.json();

		if (result.status !== 'ok') {
			throw new Error(`Failed to schedule ${sharedLocationUuid}, status: ${response.status}`);
		}

		return result;
	}

	async redirectOrder(teamId: number, sharedLocationUuid: string) {
		const url = this.getRedirectUrl(sharedLocationUuid);
		const response = await this.post(url, { team_id: teamId });

		if (!response.ok) {
			throw new Error(`Failed to redirect ${sharedLocationUuid}, status: ${response.status}`);
		}

		const result = await response.json();

		if (result.success !== true) {
			throw new Error(`Failed to redirect ${sharedLocationUuid}, status: ${response.status}`);
		}

		return result;
	}

	async sendNote(noteParams: SendNoteParams, sharedLocationUuid: string) {
		const response = await this.post(this.getSendNoteUrl(sharedLocationUuid), noteParams);

		if (!response.ok) {
			throw new Error(`Failed to send note, shared location ${sharedLocationUuid}, status: ${response.status}`);
		}

		return response.json();
	}

	async updateWayPointLocation({ taskId, sharedLocationUuid, position, findMeToken }: UpdateWayPointLocation) {
		const { coords } = position;

		const body = {
			position: { coords: { latitude: coords.latitude.toString(), longitude: coords.longitude.toString() } },
			find_me_token: findMeToken
		};

		return this.post(this.getUpdateWayPointLocationUrl(taskId.toString(), sharedLocationUuid), body);
	}

	async forward(forwardParams: ForwardSharedLocationParams) {
		return this.post(this.getForwardUrl(forwardParams.uuid), forwardParams);
	}

	async acceptForward(sharedLocationUuid: string) {
		return this.post(this.getForwardedAcceptUrl(sharedLocationUuid), { uuid: sharedLocationUuid });
	}

	async declineForward(sharedLocationUuid: string) {
		return this.post(this.getForwardedDeclineUrl(sharedLocationUuid), { uuid: sharedLocationUuid });
	}

	async postRating({ rating, reason_ids, comment, sharedLocationUuid, token }: RateParams) {
		let ratingBody = {
			rating,
			uuid: sharedLocationUuid,
			token: token
		};

		if (!_isEmpty(reason_ids)) {
			ratingBody['rating_reason_ids'] = reason_ids;
		}

		if (!_isEmpty(comment)) {
			ratingBody['comment'] = comment;
		}

		const rateUrl = this.getRatingReasonUrl(sharedLocationUuid);
		const response = await this.post(rateUrl, ratingBody);

		if (!response.ok) {
			throw new Error(`Failed to post rate ${sharedLocationUuid}, status: ${response.status}`);
		}

		const result = await response.json();

		if (result.success !== true) {
			throw new Error(`Failed to post rate ${sharedLocationUuid}, status: ${response.status}`);
		}
		return result;
	}

	async postEta(eta: dayjs.Dayjs, activityType: DriverActivity, sharedLocationUuid: string, actionToken: string) {
		const params = {
			eta: eta.toISOString(),
			activity_type: activityType,
			action_token: actionToken
		};
		const etaUrl = this.getEtaUrl(sharedLocationUuid);

		const response = await this.post(etaUrl, params);

		if (!response.ok) {
			throw new Error(`Failed to update eta for ${sharedLocationUuid}, status: ${response.status}`);
		}

		const result = await response.json();

		if (result.success !== true) {
			throw new Error(`Failed to post rate reason ${sharedLocationUuid}, status: ${response.status}`);
		}
		return result;
	}

	async postOptInOutContactNotifications(
		contactNotifications: ContactNotificationsOptions,
		sharedLocationUuid: string
	) {
		const optInOutContactNotificationsUrl = this.getOptInOutContactNotificationsUrl(sharedLocationUuid);

		const response = await this.post(optInOutContactNotificationsUrl, contactNotifications);

		if (!response.ok) {
			throw new Error(
				`Failed to post contact notifications for ${sharedLocationUuid}, status: ${response.status}`
			);
		}

		const result = await response.json();

		if (result.success !== true) {
			throw new Error(
				`Failed to post contact notifications for ${sharedLocationUuid}, status: ${response.status}`
			);
		}
		return result;
	}

	async customerCheckIn(
		sharedLocationUuid: string,
		actionToken: string,
		customerFlowType: CustomerFlowType,
		vehicle?: CarArrivedData
	) {
		const params = {
			action_token: actionToken,
			customer_flow_type: customerFlowType,
			vehicle
		};
		const checkInUrl = this.getCheckInUrl(sharedLocationUuid);

		const response = await this.post(checkInUrl, params);

		if (!response.ok) {
			throw new Error(`Failed to checkin for ${sharedLocationUuid}, status: ${response.status}`);
		}

		const result = await response.json();

		if (result.success !== true) {
			throw new Error(`Failed to post checkin reason ${sharedLocationUuid}, status: ${response.status}`);
		}
		return result;
	}

	async customerCheckOut(sharedLocationUuid: string, actionToken: string) {
		const params = {
			action_token: actionToken
		};
		const checkOutUrl = this.getCheckOutUrl(sharedLocationUuid);

		const response = await this.post(checkOutUrl, params);

		if (!response.ok) {
			throw new Error(`Failed to post checkout for ${sharedLocationUuid}, status: ${response.status}`);
		}

		const result = await response.json();

		if (result.success !== true) {
			throw new Error(`Failed to post checkout for ${sharedLocationUuid}, status: ${response.status}`);
		}
		return result;
	}

	public async submitQuestionnaire(answers: any, url: string): Promise<Response> {
		const response = await this.post(url, answers);
		return response;
	}

	private getUpdateWayPointLocationUrl(taskId: string, sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/api/customer/task/${taskId}/find_me/${sharedLocationUuid}`;
	}

	private getForwardUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/forward/${sharedLocationUuid}`;
	}

	private getForwardedDeclineUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/forward/decline/${sharedLocationUuid}`;
	}

	private getForwardedAcceptUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/forward/accept/${sharedLocationUuid}`;
	}

	private getSendNoteUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/api/notes/${sharedLocationUuid}`;
	}

	private getRatingReasonUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/api/rating_reason/${sharedLocationUuid}`;
	}

	private getEtaUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/update_eta/${sharedLocationUuid}`;
	}

	private getCheckInUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/check_in/${sharedLocationUuid}`;
	}

	private getCheckOutUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/check_out/${sharedLocationUuid}`;
	}

	private getScheduleUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/reschedule/reschedule/${sharedLocationUuid}`;
	}

	private getSharedLocationUrl(sharedLocationUuid: string): string {
		return `${this.getBaseUrl()}/shared/${sharedLocationUuid}?full=true`;
	}

	private getRedirectUrl(sharedLocationUuid: string): string {
		return `${this.getBaseUrl()}/shared/redirect_delivery/${sharedLocationUuid}`;
	}

	private getVehiclesUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/vehicles/${sharedLocationUuid}`;
	}

	private getMaskedPhoneUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/${sharedLocationUuid}/phone_number`;
	}

	private getOptInOutContactNotificationsUrl(sharedLocationUuid: string) {
		return `${this.getBaseUrl()}/shared/opt_in_out/${sharedLocationUuid}`;
	}

	private getBaseUrl() {
		return this.config.real_time_url;
	}

	private get headers() {
		const token = getEnv<RootEnv>().tokenService.token;

		let headers = {
			'Content-Type': 'application/json'
		};

		if (token) {
			headers['Authorization'] = `Token token=${token}`;
		}

		return headers;
	}

	private post(url, body) {
		return fetch(url, {
			method: 'POST',
			body: JSON.stringify(body),
			headers: this.headers
		});
	}

	private get config(): Region {
		const { regionService } = getEnv<RootEnv>();
		return regionService.getConfigByRegion();
	}
}
